import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { batch, useDispatch, useSelector } from 'react-redux';
import { flatten, uniq } from 'lodash';
import { Icon, Modal, Tappable } from '@vectord/components';
import { TypeUtils } from '@vectord/utils';
import cogoToast from 'cogo-toast';

import {
  bulkRescheduleValidateErrorMessageSelector,
  isHybridContest,
  RESCHEDULE_CLASSES_STAGES,
  RESCHEDULE_CLASSES_PAGE_TRACKINGS,
  rescheduleClassesStageSelector,
  resetRescheduleClassesForm,
  resetRescheduleFilters,
  setRescheduleClassesFormErrors,
  setRescheduleClassesStage,
  useBulkUpdateSbatGroupsMutation,
  useFilteredRescheduleClasses,
  validateRevisedSchedule,
} from 'features/classCreation';
import analytics from 'utils/analytics';

function RescheduleValidation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { superBatchId } = useParams();
  const state = useSelector((state) => state.rescheduleClassesForm);
  const errorMessage = useSelector(bulkRescheduleValidateErrorMessageSelector);

  const [bulkUpdateSbatGroups, { isSuccess, error, isLoading: isUpdating }] =
    useBulkUpdateSbatGroupsMutation();
  const stage = useSelector(rescheduleClassesStageSelector);
  const isModalOpen = stage !== RESCHEDULE_CLASSES_STAGES.notOpen;

  const handleModalClose = useCallback(() => {
    dispatch(setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.confirmation));
  }, [dispatch]);

  const filteredSbatGroups = useFilteredRescheduleClasses(
    Object.values(state.sbatGroups)
  );

  const handleCancel = useCallback(() => {
    dispatch(setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.confirmation));
  }, [dispatch]);

  const handleConfirm = useCallback(() => {
    if (isUpdating) return;

    const superBatchNames = uniq(
      flatten(
        Object.values(state.sbatGroups).map((sbatGroup) =>
          sbatGroup.super_batch_academy_topics.map(
            (sbat) => sbat.super_batch.name
          )
        )
      )
    );
    const retainedClasses = Object.values(state.sbatGroups).filter(
      (sbatGroup) => !sbatGroup.locked
    );
    const filteredClasses = filteredSbatGroups.filter(
      (sbatGroup) => sbatGroup.locked
    );
    const lockedClasses = Object.values(state.sbatGroups).filter(
      (sbatGroup) => sbatGroup.locked
    );
    let hybridContestNames = [];
    const sbatGroups = state.sbatGroups;
    Object.values(sbatGroups).forEach((sbatGroup) => {
      const { academy_topic: academyTopic } = sbatGroup;
      if (isHybridContest(academyTopic.lessons)) {
        hybridContestNames.push(academyTopic.title);
      }
    });

    analytics.click({
      click_type: RESCHEDULE_CLASSES_PAGE_TRACKINGS.confirmReschedule,
      custom: {
        num_of_retained_classes: retainedClasses.length,
        num_of_filtered_classes: filteredClasses.length,
        num_of_locked_classes: lockedClasses.length,
        locked_class_ids: lockedClasses.map((o) => o.id),
        retained_class_ids: retainedClasses.map((o) => o.id),
        super_batch_names: superBatchNames,
        hybrid_contest_count: hybridContestNames.length,
        hybrid_contest_name: hybridContestNames,
      },
    });

    const errors = validateRevisedSchedule(state);
    if (Object.keys(errors).length > 0) {
      dispatch(setRescheduleClassesFormErrors(errors));
    } else {
      dispatch(setRescheduleClassesFormErrors({}));
      bulkUpdateSbatGroups(state);
    }
  }, [bulkUpdateSbatGroups, dispatch, filteredSbatGroups, isUpdating, state]);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Classes have been rescheduled successfully!');
      batch(() => {
        dispatch(resetRescheduleClassesForm());
        dispatch(resetRescheduleFilters());
        dispatch(setRescheduleClassesStage(RESCHEDULE_CLASSES_STAGES.view));
      });
      navigate(`/classes/view-schedule/${superBatchId}`);
    }
  }, [
    dispatch,
    filteredSbatGroups,
    isSuccess,
    navigate,
    state.academyTopics,
    state.sbatGroups,
    superBatchId,
  ]);

  useEffect(() => {
    if (error) {
      const message = error?.data?.message;

      if (message?.base && TypeUtils.isArray(message.base)) {
        dispatch(setRescheduleClassesFormErrors({ base: message.base }));
      } else {
        cogoToast.error(
          message ||
            'Failed to reschedule classes. Please try again after sometime!'
        );
      }
    }
  }, [dispatch, error]);

  return (
    <Modal
      isOpen={isModalOpen}
      size="large"
      onClose={handleModalClose}
      withoutHeader
      unMountOnClose
      className="p-m"
    >
      <div className="column justify-c align-c">
        <div className="row">
          <Icon
            name="warning"
            className="h2 m-r-xxs danger"
          />
          <div className="dark h4 text-c m-b-m">{errorMessage}</div>
        </div>
        <div>
          <Tappable
            className="btn btn-long btn-danger"
            onClick={handleConfirm}
            disabled={isUpdating}
          >
            Yes
          </Tappable>
          <Tappable
            className="btn btn-long m-l-xxs"
            onClick={handleCancel}
            disabled={isUpdating}
          >
            No
          </Tappable>
        </div>
      </div>
    </Modal>
  );
}

export default RescheduleValidation;
